import { Component, Input, OnInit } from "@angular/core";
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { MatSelectChange } from "@angular/material/select";
import { isEmpty } from "lodash";
import { Observable, Subscription, of } from "rxjs";
import { debounceTime, distinctUntilChanged, finalize, map, startWith, switchMap, tap } from "rxjs/operators";
import { PaginatedResponse } from "../../../core/http/paginated-response.type";
import { AnyObject } from "../../types/app.type";
import { Operator, Property, PropertyList, SearchOption } from "./property-list.type";

@Component({
  selector: "app-property-list",
  templateUrl: "./property-list.component.html",
  styleUrls: ["./property-list.component.scss"],
})
export class PropertyListComponent implements OnInit {
  @Input() config: PropertyList;

  form: FormArray;
  subscriptions: Subscription[] = [];
  options: AnyObject<Observable<unknown>> = {};

  constructor(private fb: FormBuilder) {}

  //#region Lifecycle Methods

  ngOnInit(): void {
    this.form = this.fb.array([]);
    this.addProperty();
  }

  //#endregion

  //#region Handlers Methods

  onAddProperty(): void {
    this.addProperty();
  }

  onRemoveProperty(index: number): void {
    this.removeProperty(index);
  }

  onChangeProperty(_change: MatSelectChange, control: FormGroup): void {
    const operator = control.value.operator as Operator;
    const property = control.value.property as Property<any>;

    const hasValidOperator = property.operators.includes(operator);

    if (hasValidOperator) {
      this.updateValidators(operator, control);
    } else {
      this.resetOperator(control);
    }

    // Update search control of a property
    if (property.control === "search") {
      this.updateSearchControl(control);
    }

    this.resetValue(control);
  }

  onChangeOperator(change: MatSelectChange, control: FormGroup): void {
    this.updateValidators(change.value as Operator, control);
  }

  onSubmit(): void {
    // this.submit();
  }

  //#endregion

  //#region Other Methods

  /** Get the properties to display in the property dropdown */
  getPropertiesToDisplay = (currentProperty: any): Property<AnyObject<any>>[] => {
    const selectedProperties =
      (this.form?.value.map((value) => value.property?.key).filter((value) => value) as string[]) || [];

    return this.config.properties.filter(
      (property) => !(selectedProperties.includes(property?.key) && property?.key !== currentProperty?.key),
    );
  };

  /** Value to be displayed on search inputs */
  searchInputLabel(option: SearchOption): string {
    if (option === null) {
      return null;
    }
    const key = Object.keys(option).find((k) => k !== "id");
    return option[key];
  }

  /** Add a new property to the list */
  private addProperty(): void {
    const propertyFormGroup = this.fb.group({
      property: [null, Validators.required],
      operator: [null, Validators.required],
      value: [{ value: null, disabled: true }],
    });

    const operatorSubscription = propertyFormGroup.controls.operator.valueChanges.subscribe((value) => {
      if (value === null) {
        propertyFormGroup.controls.value.disable();
      } else {
        propertyFormGroup.controls.value.enable();
        propertyFormGroup.controls.value.updateValueAndValidity();
      }
    });

    this.subscriptions.push(operatorSubscription);

    this.form.push(propertyFormGroup);
  }

  /** Remove a property by index from the list */
  private removeProperty(index: number): void {
    this.subscriptions[index].unsubscribe();
    this.subscriptions.splice(index, 1);
    this.form.removeAt(index);
  }

  /** Update the property validators */
  private updateValidators(operator: Operator | null, control: FormGroup): void {
    const validators = (control.value.property as Property).validators?.(operator) || [];

    control.controls.value.setValidators(validators);
    control.controls.value.updateValueAndValidity();
  }

  /** Update the search control of a property */
  private updateSearchControl(control: FormGroup): void {
    const property = control.controls.property.value as Property<any>;
    this.options[property.key as string] = (control.controls.value as FormControl).valueChanges.pipe(
      startWith(""),
      distinctUntilChanged(),
      tap(() => (property.search.loading = true)),
      debounceTime(400),
      switchMap((formValue) => {
        if (formValue === null) {
          formValue = "";
        }

        const shouldFetchData = !isEmpty(formValue) || (isEmpty(formValue) && property.search.fetchDefaultData);

        const propToDisplay = property.search.propToDisplay;

        if (shouldFetchData) {
          return property.search
            .service(formValue)
            .pipe(
              map((response: PaginatedResponse<any>) =>
                response.items.map((item) => ({ id: item.id, [propToDisplay]: item[propToDisplay] })),
              ),
            );
        } else {
          return of(null);
        }
      }),
      tap(() => (property.search.loading = false)),
      finalize(() => (property.search.loading = false)),
    );
  }

  /** Reset the property operator. If the property has only one allowed operator then it's set automatically */
  private resetOperator(control: FormGroup): void {
    const property = control.value.property;
    const operator = property.operators.length === 1 ? property.operators[0] : null;

    control.setValue({
      ...control.value,
      value: null,
      operator,
    });

    this.updateValidators(operator, control);
  }

  /** Reset the property value */
  private resetValue(control: FormGroup): void {
    control.setValue({ ...control.value, value: null });
  }

  //#endregion
}
